import {usePlugins} from '@github-ui/copilot-chat/plugin/registry'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {Blankslate} from '@primer/react/experimental'

import {ErrorFallback} from '../ErrorFallback'
import type {PreviewableContent} from './content-preview-types'
import styles from './ContentPreviewItem.module.css'
import {CreateIssuePreview} from './create-issue-preview/CreateIssuePreview'
import {ExistingIssuePreview} from './create-issue-preview/ExistingIssuePreview'
import {UpdateIssuePreview} from './create-issue-preview/UpdateIssuePreview'
import {DiffPreview} from './DiffPreview'
import {FilePreview} from './FilePreview'
import {ImagePreview} from './ImagePreview'
import {IssuePreview} from './IssuePreview'
import {JobResultPreview} from './JobResultPreview'

interface ContentPreviewItemProps {
  isOpening: boolean
  onClose: (closePreview?: boolean) => void
  previewItem: PreviewableContent
}

function downloadUrl(value: string | undefined) {
  if (!value) return
  try {
    const url = new URL(value, ssrSafeLocation.origin || 'https://github.com')
    if (url.protocol === 'https:' || url.protocol === 'http:') return url.href
  } catch {
    return
  }
}

export function ContentPreviewItem({isOpening, onClose, previewItem}: ContentPreviewItemProps) {
  const plugins = usePlugins()
  const sourceUrl = previewItem.type === 'file' ? downloadUrl(previewItem.sourceUrl) : undefined
  let item = null
  switch (previewItem.type) {
    case 'file':
      item = previewItem.unavailableReason ? (
        <div className={styles.unavailableState}>
          <Blankslate>
            <Blankslate.Heading as="h3">{previewItem.unavailableReason}</Blankslate.Heading>
            {sourceUrl && (
              <Blankslate.SecondaryAction href={sourceUrl}>Download {previewItem.name}</Blankslate.SecondaryAction>
            )}
          </Blankslate>
        </div>
      ) : (
        <FilePreview isPreviewOpening={isOpening} file={previewItem} onClose={onClose} />
      )
      break
    case 'file-diff':
      item = <DiffPreview fileDiff={previewItem} />
      break
    case 'issue':
      item = <IssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'new-issue':
      item = <CreateIssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'update-issue':
      item = <UpdateIssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'existing-issue':
      item = <ExistingIssuePreview isPreviewOpening={isOpening} issue={previewItem} onClose={onClose} />
      break
    case 'image':
      item = <ImagePreview image={previewItem} />
      break
    case 'plugin-content':
      for (const plugin of plugins) {
        const PluginPreviewComponent = plugin.previewComponents?.[previewItem.reference.type]
        if (PluginPreviewComponent) {
          item = <PluginPreviewComponent reference={previewItem.reference} />
          break
        }
      }
      break
    case 'job-result':
      item = <JobResultPreview jobResult={previewItem} />
      break
    case 'changeset-diff':
      item = previewItem.renderPreview()
      break
  }

  return item ? (
    <ErrorBoundary
      boundaryName="copilot-content-preview-item"
      fallback={<ErrorFallback regionName="This workbench item" />}
    >
      {item}
    </ErrorBoundary>
  ) : null
}
